import {html, PolymerElement} from '@polymer/polymer/polymer-element.js';
import template from './market-banner-uploader.html';
import {RunConfigService} from '@nuskin/ns-util';
import {MySiteRestService, MySiteBanner, ImageType} from '@nuskin/my-site-api';
import Cropper from 'cropperjs';

export class MarketBannerUploader extends PolymerElement {

    static get is() {
        return 'market-banner-uploader';
    }

    static get template() {
        let htmlElement = document.createElement('TEMPLATE');
        htmlElement.innerHTML = template;
        return htmlElement;
    } 

    static get properties() {
        return {
            // List of banner objects
            banners: {
                type: Array,
                value: []
            }
        };
    }

    ready() {
        super.ready();
        this._loadBanners();
    }

    connectedCallback() {
        super.connectedCallback();

        setTimeout(() => {
            let selectors = this.shadowRoot.querySelectorAll('paper-dropdown-menu');
            selectors.forEach(selector => {
                selector.addEventListener('value-changed', (e) => {
                    let color= e.detail.value;
                    let index = e.currentTarget.dataset.index;
                    if (color == undefined) return
                    if(color !== this.banners[index].scrimCopyColor)
                        this.banners[index].scrimCopyColor = color;
                });
            });
        }, 500);
        
    }

    _getSkrimColor(banner) {
        return banner.scrimCopyColor === 'dark' ? 0 : 1;
    }

    _createBanner() {
        let banner = bannerDtoToDomObj(new MySiteBanner());
        banner.scrimCopyColor = 'light';
        this.push('banners', banner);
    }

    _removeBanner(e) {
        let idx = getIndex(e);
        if ((typeof idx === 'number') && idx > -1) {
            this.splice('banners', idx, 1);
        }
    }

    _onImageInput(e) {
        let idx = getIndex(e);

        // Retrieve file object from input
        let el = this.shadowRoot.querySelector(`#imageInput-${idx}`);
        if (!el.files || el.files.length === 0) return;
        let imgFile = el.files[0];

        // Inject image into DOM and initialize cropper
        readImageAsURL(imgFile)
            .then(url => {
                this.set(`banners.${idx}.rawImage`, url);
                this.set(`banners.${idx}.editImage`, true);
                let imgEl = this.shadowRoot.querySelector(`#cropperImage-${idx}`);
                this.banners[idx].cropper = new Cropper(imgEl, {
                    viewMode: 2
                });
            });
    }

    _closeCropper(e) {
        let idx = getIndex(e);
        this.set(`banners.${idx}.rawImage`, null);
        this.set(`banners.${idx}.editImage`, false);

        if (this.banners[idx].cropper) {
            this.banners[idx].cropper.destroy();
            this.banners[idx].cropper = null;
        }

        this.shadowRoot.querySelector(`#imageInputForm-${idx}`).reset();
    }

    _saveCropper(e) {
        let idx = getIndex(e);
        let banner = this.banners[idx];

        // Get image canvases sized to the final image requirements of the ns-hero component
        let smallCanvas = banner.cropper.getCroppedCanvas({height: 250});
        let largeCanvas = banner.cropper.getCroppedCanvas({height: 450});

        // Create image data urls to use in banner preview
        let urlGroup = {
            mobile: smallCanvas.toDataURL(),
            tablet: largeCanvas.toDataURL(),
            desktop: largeCanvas.toDataURL()
        };

        // Create image blobs to upload to Firebase
        let country = RunConfigService.getRunConfig().country;
        let language = RunConfigService.getRunConfig().language;
        let namePrefix = `${language}_${country}-${randomId()}`;
        let blobPromises = [
            createImageBlob(smallCanvas, `${namePrefix}-mobile`),
            createImageBlob(largeCanvas, `${namePrefix}-tablet`),
            createImageBlob(largeCanvas, `${namePrefix}-desktop`)
        ];
        Promise.all(blobPromises)
            .then(blobs => ({mobile: blobs[0], tablet: blobs[1], desktop: blobs[2]}))
            .then(blobGroup => {
                // Save data to banner object and close the modal
                this.set(`banners.${idx}.adaptiveImageSet`, urlGroup);
                this.set(`banners.${idx}.imageBlobs`, blobGroup);
                this._closeCropper({currentTarget: {dataIdx: idx}});
            });
    }

    _isFirstBanner(idx) {
        return idx === 0;
    }

    _setDefaultBanner(e) {
        let idx = getIndex(e);
        let banner = this.banners[idx];

        if (banner) {
           this.splice('banners', idx, 1);
           this.unshift('banners', banner);
        }
    }

    _loadBanners() {
        MySiteRestService.getMarketBanners().then(data => {
            let banners = data.map(b => bannerDtoToDomObj(b));
            this.set('banners', banners);
        });
    }

    async _saveBanners() {
        // Show upload progress overlay
        this.$['overlay'].show();

        // Upload new banner images, if any
        let newImages = this.banners.filter(b => b.imageBlobs) || []
            
        for(let i=0; i<newImages.length; i++) {
            let urlPkg = await uploadImagePackage(newImages[i].imageBlobs)
            newImages[i].adaptiveImageSet = urlPkg
            newImages[i].imageUrls = urlPkg;
        }
        // Save changes to banners
        let bannerDtos = this.banners.map(b => domObjToBannerDto(b));
        await MySiteRestService.saveMarketBanners(bannerDtos);
        // Reload page to show updated banners
        this._reload() 
    }

    _reload() {
        this.$['overlay'].hide();
        location.reload();
    }
}

customElements.define(MarketBannerUploader.is, MarketBannerUploader);


//=================================================================//
//
//  UTILITY FUNCTIONS
//
//=================================================================//

function bannerDtoToDomObj(dto) {
    let obj = Object.assign({}, dto);
    obj.adaptiveImageSet = dto.imageUrls || {
        mobile: '',
        tablet: '',
        desktop: ''
    };
    obj.translations = {ctaCopy: dto.ctaText};
    obj.editImage = false;
    return obj;
}

function domObjToBannerDto(obj) {
    let dto = new MySiteBanner(obj);
    dto.imageUrls = obj.adaptiveImageSet;
    dto.ctaText = obj.translations.ctaCopy;
    return dto;
}

function randomId() {
    const s4 = () => Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    return `${s4()}${s4()}${s4()}`;
}

function createImageBlob(canvas, name) {
    return new Promise(res => canvas.toBlob(blob => {
        res(new File([blob], name, {fileType: blob.type}));
    }, 'image/jpeg', 0.75));
}
 
async function uploadImagePackage(pkg) {
    let urlPkg = {};
    let keys = Object.keys(pkg)
    for(let i=0; i< keys.length; i++) {
        let key = keys[i]
        let blob = pkg[key]
        let url = await MySiteRestService.uploadImage(blob, ImageType.MARKET_BANNER, true)
        urlPkg[key] = url
    }
    return urlPkg
}

function readImageAsURL(imgFile) {
    return new Promise(res => {
        let reader = new FileReader();
        reader.onload = e => res(e.target.result);
        reader.readAsDataURL(imgFile);
    });
}

function getIndex(e) {
    return e.currentTarget.dataIdx;
}
